/**
 * @architecture-header 1
 * @layer app
 * @domain workspace
 * @responsibility Provide host-injected browser or desktop capabilities to authenticated modules.
 * @state-owner React provider immutable host configuration
 * @allowed-imports React; @vispo/api-client
 * @forbidden direct Vite env; Electron globals; module state
 * @public-api via @vispo/workspace-web
 * @side-effects context reads only
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 * @environment-scope multi-environment
 * @credential-scope runtime-selected
 * @destructive-action none
 */

import type { VispoApiClient } from '@vispo/api-client';
import { createContext, type PropsWithChildren, useContext } from 'react';
import type { SeatStatus } from '../../shared/lib/model';

export interface WorkspaceSession {
  userId: string;
  email: string;
  displayName: string | null;
}

export interface WorkspaceAuthAdapter {
  getSession(): Promise<WorkspaceSession | null>;
  subscribe(listener: (session: WorkspaceSession | null) => void): () => void;
  signInWithPassword(credentials: { email: string; password: string }): Promise<void>;
  signInWithGoogle(): Promise<void>;
  signOut(): Promise<void>;
}

export interface WorkspacePresenceRecord {
  userId: string;
  studentName: string;
  /** The one seat vocabulary the product speaks; hosts translate into it, never past it. */
  status: SeatStatus;
  examId?: string;
  examTitle?: string;
  phase?: string;
  currentQuestionIndex?: number;
  totalQuestions?: number;
  confusionAlerts?: Array<{
    type: 'stuck' | 'modified' | 'manual';
    questionIndex: number;
    timestamp: string;
  }>;
  lastActiveAt: string;
}

export interface WorkspaceRealtimeAdapter {
  subscribeClassPresence(
    classId: string,
    listener: (snapshot: { connected: boolean; records: WorkspacePresenceRecord[] }) => void,
  ): Promise<() => void>;
  trackClassPresence(classId: string, record: WorkspacePresenceRecord): Promise<() => void>;
}

export interface WorkspaceEnvironment {
  apiClient: VispoApiClient;
  auth: WorkspaceAuthAdapter;
  realtime: WorkspaceRealtimeAdapter;
  platform: 'browser' | 'desktop';
  demoMode: boolean;
  openExternal(url: string): Promise<void>;
}

const EnvironmentContext = createContext<WorkspaceEnvironment | null>(null);

export function EnvironmentProvider({
  children,
  environment,
}: PropsWithChildren<{ environment: WorkspaceEnvironment }>) {
  return <EnvironmentContext.Provider value={environment}>{children}</EnvironmentContext.Provider>;
}

export function useWorkspaceEnvironment(): WorkspaceEnvironment {
  const value = useContext(EnvironmentContext);
  if (!value) throw new Error('WorkspaceEnvironment is missing');
  return value;
}
